import { singleFetch } from 'components/grid/grid-ctx/ctx-utils';
import Map from 'ol/Map';
import View from 'ol/View';
import WKT from 'ol/format/WKT';
import { Circle, Point, SimpleGeometry } from 'ol/geom';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import 'ol/ol.css';
import { OSM, Vector as VectorSource } from 'ol/source';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { EadNode, OriginatorBody } from 'types';
import {
  constructEadMapLink,
  constructMapLinkLabel,
  constructOriginatorMapLink,
} from './mapUtils';
import { SelectField } from 'components/select-field/select-field';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import { useOriginatorDetail } from 'modules/register/register-api';

const MapRender: React.FC<{ coords: string }> = ({ coords }) => {
  const mapRef = useRef<Map | null>(null);
  const mapDivElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const raster = new TileLayer({
      source: new OSM(),
    });
    mapRef.current = null;
    const format = new WKT();
    const features = [
      format.readFeature(coords, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      }),
    ];

    if (
      features &&
      features[0] &&
      features?.[0]?.getGeometry?.()?.getType?.() === 'Point'
    ) {
      const feature = features[0];

      feature.setGeometry(
        new Circle(
          ((feature?.getGeometry?.() as unknown) as Point).getCoordinates(),
          10
        )
      );
    }

    const vector = new VectorLayer({
      source: new VectorSource({
        features,
      }),
      style: new Style({
        stroke: new Stroke({ width: 3 }),
      }),
    });

    mapRef.current = new Map({
      layers: [raster, vector],
      target: mapDivElementRef.current as HTMLDivElement,
      view: new View(),
    });

    const view = mapRef.current.getView();
    const polygon = features[0].getGeometry()!;
    view.fit(polygon.getSimplifiedGeometry(3) as SimpleGeometry, {
      maxZoom: 18,
    });
  }, [coords]);

  return (
    <div
      style={{
        width: '100%',
        height: 'calc(100vh - 170px)',
        padding: 8,
      }}
      ref={mapDivElementRef}
    />
  );
};

const aidPrefix = '/api/archival-aid-metadata';

const MapPage = () => {
  const { id, index } = useParams<{
    id: string;
    index: string;
  }>();
  const { push } = useHistory();
  const intIndex = parseInt(index);
  const [item, setItem] = useState<EadNode>();

  useEffect(() => {
    (async () => {
      const item = await singleFetch<EadNode>(
        `${aidPrefix}/ead/${id}`,
        `ead-detail-${id}`
      );
      setItem(item);
    })();
  }, [id]);

  if (!item) {
    return <></>;
  }
  const options = item.coordinates.map((c, i) => {
    const label = constructMapLinkLabel(c);
    return {
      id: constructEadMapLink(item.id, i),
      label: `${label.labelPrefix} ${label.linkLabel}`,
    };
  });

  return (
    <div>
      <div
        style={{
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          maxWidth: 400,
          maxHeight: 90,
          height: 90,
          minHeight: 90,
        }}
      >
        <SelectField
          label="Dostupné souřadnice:"
          name="coordinates"
          value={constructEadMapLink(item.id, intIndex)}
          onChange={(value) => push(value)}
          items={options}
        />
      </div>
      <MapRender key={index} coords={item.coordinates[intIndex]} />
    </div>
  );
};

const originatorCoords: Record<
  number,
  keyof Pick<OriginatorBody, 'coordBorder' | 'coordPoint'>
> = {
  0: 'coordPoint',
  1: 'coordBorder',
};

export const MapPageOriginator = () => {
  const { id, index } = useParams<{
    id: string;
    index: string;
  }>();
  const { push } = useHistory();
  const intIndex = parseInt(index);
  const response = useOriginatorDetail(id);

  if (response.isLoading) {
    return <></>;
  }

  const item = response.data;

  if (!item) {
    return <></>;
  }

  const options = Object.keys(originatorCoords)
    .filter((k) => item.body[originatorCoords[(k as unknown) as number]])
    .map((key) => {
      const coord = item.body[originatorCoords[(key as unknown) as number]];
      const label = constructMapLinkLabel(coord);
      return {
        id: constructOriginatorMapLink(item.id, (key as unknown) as number),
        label: `${label.labelPrefix} ${label.linkLabel}`,
      };
    });

  const key = originatorCoords[intIndex];

  return (
    <div>
      <div
        style={{
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          maxWidth: 400,
          maxHeight: 90,
          height: 90,
          minHeight: 90,
        }}
      >
        <SelectField
          label="Dostupné souřadnice:"
          name="coordinates"
          value={constructOriginatorMapLink(item.id, intIndex)}
          onChange={(value) => push(value)}
          items={options}
        />
      </div>
      <MapRender key={index} coords={item.body[key]} />
    </div>
  );
};

export default MapPage;
